import React, { useCallback } from "react";
import { useT } from "@transifex/react";
import { Modal } from "storybook/components/modal";
import { InitialModal } from "./initial-modal";
import { DeclineModal } from "./decline-modal";
import { ThankYouModal } from "./thank-you-modal";
import { ScheduleModal } from "./schedule-modal";
import { LockModal } from "../lock-modal";
import { ModalType, usePatientInterest } from "context/patient-interest";
import { GlobalModalStyles } from "./styles";

enum ModalTitle {
  INITIAL = "initial",
  DECLINE = "decline",
  THANK_YOU = "thank-you",
  EMAIL = "email",
  CALENDAR = "calendar",
  CONFIRMATION = "confirmation",
  LOCK_MODAL = "lock-modal",
}

const getModalTitle = (modalType: ModalType, t: any) => {
  const defaultTitle = t("Speak to a Specialist");

  switch (modalType) {
    case ModalType.INITIAL:
      return defaultTitle;
    case ModalType.DECLINE:
      return t("Please select a reason");
    case ModalType.THANK_YOU:
      return t("Thank you for completing the survey");
    case ModalType.EMAIL:
      return t("Schedule Appointment");
    case ModalType.CALENDAR:
      return t("When are you available?");
    case ModalType.CONFIRMATION:
      return t("Thank you");
    case ModalType.LOCK_RESTART:
      return "";
    case ModalType.LOCK_SCHEDULER:
      return "";
    case ModalType.LOCK_PATIENT_HUB:
      return "";
    default:
      return defaultTitle;
  }
};

const StepModalContents = {
  [ModalType.INITIAL]: InitialModal,
  [ModalType.DECLINE]: DeclineModal,
  [ModalType.THANK_YOU]: ThankYouModal,
  [ModalTitle.EMAIL]: ScheduleModal,
  [ModalTitle.CALENDAR]: ScheduleModal,
  [ModalTitle.CONFIRMATION]: ScheduleModal,
  [ModalType.LOCK_RESTART]: LockModal,
  [ModalType.LOCK_SCHEDULER]: LockModal,
  [ModalType.LOCK_PATIENT_HUB]: LockModal,
};

const getModalContents = (modalType?: ModalType) => {
  if (modalType) {
    return StepModalContents[modalType];
  }

  return null;
};

type Props = {
  riskResults: any;
  isRemote: boolean;
};

export function InterestModal({ riskResults, isRemote }: Props) {
  const { modalType, setModalType } = usePatientInterest();
  const t = useT();

  const ModalContents = getModalContents(modalType);
  const title = getModalTitle(modalType || ModalType.INITIAL, t);

  const handleClose = useCallback(() => {
    setModalType(undefined);
  }, [setModalType]);

  if (!modalType || !ModalContents) {
    return null;
  }

  const isLockModal =
    modalType === ModalType.LOCK_RESTART ||
    modalType === ModalType.LOCK_SCHEDULER ||
    modalType === ModalType.LOCK_PATIENT_HUB;

  return (
    <>
      <GlobalModalStyles />
      <Modal
        theme="default"
        padding="25px"
        width="700px"
        onClose={handleClose}
        open={Boolean(modalType)}
        disableBackdropClick={!isLockModal}
        hideCloseButton={isLockModal}
      >
        <Modal.Head alignTitle="left">{title}</Modal.Head>
        <ModalContents
          onClose={handleClose}
          riskResults={riskResults}
          isRemote={isRemote}
        />
      </Modal>
    </>
  );
}
